package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initDispatcherParallelismOnKotlinNativePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/DispatcherParallelismOnKotlinNative.md", FrontMatterElement.Builder { addScalar("title", "Dispatcher parallelism and the memory of a Kotlin/Native service"); addScalar("searchTitle", "Kotlin/Native memory — limitedParallelism, thread count and RSS in a Ktor CIO service"); addScalar("date", "2026-09-15"); addScalar("description", "Resident memory on Kotlin/Native is the thread count times half a megabyte. A forked Ktor CIO on a fixed thread pool holds 200 connections on eight threads and 16 MB — fewer threads and a lower p99 than Go on the same machine."); }))
}

@Page("/blog/dispatcher-parallelism-on-kotlin-native")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun DispatcherParallelismOnKotlinNativePage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/under-a-container-limit") {
            org.jetbrains.compose.web.dom.Text("The previous post")
        }
        org.jetbrains.compose.web.dom.Text(" ended with a Ktor service holding 61 MB under load with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=16")
        }
        org.jetbrains.compose.web.dom.Text(" set, and with an unfinished sentence: the memory that is left is not heap, it is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("per-thread allocator pages")
        }
        org.jetbrains.compose.web.dom.Text(", so the remaining lever is the number of threads. This is that measurement, and it goes further than I expected when I started writing it. Capping the dispatcher the CIO engine runs on takes the service from 67 MB to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("36 MB")
        }
        org.jetbrains.compose.web.dom.Text(" at 200 connections. Then it turns out the cap was reaching only half of what spawns threads, and fixing the other half takes it to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("16 MB on eight threads")
        }
        org.jetbrains.compose.web.dom.Text(" — fewer threads and a lower p99 than Go on the same machine on the same afternoon.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nothing here is filed upstream and nothing here is a patch you can depend on. There is a section below saying exactly why, and the reason gets sharper as the numbers get better. What ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("is")
        }
        org.jetbrains.compose.web.dom.Text(" usable today is the rule underneath: on Kotlin/Native, resident memory is a function of how many threads a process has run work on, and the thread count is something you can decide.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-stand") }) {
        org.jetbrains.compose.web.dom.Text("The stand")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same as the previous post: two dedicated hosts, four cores and 7.6 GiB each, Ubuntu 26.04, subject and generator on separate machines. Kotlin 2.4.10 ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" release binaries, Ktor 3.5.2 with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-server-cio")
        }
        org.jetbrains.compose.web.dom.Text(", one route returning one JSON object, k6, 30 s per run, memory bounded by a cgroup and read from it. Every arm carries the merged lock fix (")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KTOR-9891") {
            org.jetbrains.compose.web.dom.Text("KTOR-9891")
        }
        org.jetbrains.compose.web.dom.Text(") and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=16")
        }
        org.jetbrains.compose.web.dom.Text(", so what follows is on top of everything the previous post recommended.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("resident-memory-is-the-thread-count") }) {
        org.jetbrains.compose.web.dom.Text("Resident memory is the thread count")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc/<pid>/smaps")
        }
        org.jetbrains.compose.web.dom.Text(" under load, split by mapping:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("threads")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("binary (file-backed)")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("anonymous")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("total RSS")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("idle, no request ever served")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.89 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("2.00 MB")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.0 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("12 connections")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("41")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.05 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("29.50 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("35.7 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("200 connections")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("105")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.07 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("64.99 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("71.2 MB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two things fall out. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The binary is the only constant")
        }
        org.jetbrains.compose.web.dom.Text(" — file-backed resident memory is 5.89 to 6.08 MB in every dump taken across this work, idle or loaded, ten threads or a hundred and seven, and it is clean and shared. Everything else is anonymous, and between the two loaded points it grows at ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("0.555 MB per thread")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("An idle process tells you nothing about a loaded one.")
        }
        org.jetbrains.compose.web.dom.Text(" Eight megabytes at rest and thirty-six under two hundred connections is the same binary on the same machine. The per-thread cost is paid not when a thread is created but when it first allocates — that is when the allocator fills its page cache for a size class, and it never gives those pages back. Size a container from a loaded process or you will size it four times too small.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Those threads are not busy, either. Sampling ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc/<pid>/task/*/wchan")
        }
        org.jetbrains.compose.web.dom.Text(" under full load, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("27 to 34 of 36 threads are parked in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("futex_do_wait")
            }
        }
        org.jetbrains.compose.web.dom.Text(" at any instant, one to nine runnable. The thread count is a cache of parked workers, which is why it multiplies with a per-thread page cache into an RSS.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("capping-the-engine-s-dispatcher") }) {
        org.jetbrains.compose.web.dom.Text("Capping the engine's dispatcher")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(" on Kotlin/Native is this, in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlinx-coroutines-core/native/src/Dispatchers.kt")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""internal object DefaultIoScheduler : CoroutineDispatcher() {
    // 2048 is an arbitrary KMP-friendly constant
    private val unlimitedPool = newFixedThreadPoolContext(2048, "Dispatchers.IO")
    private val io = unlimitedPool.limitedParallelism(64) // Default JVM size

    override fun limitedParallelism(parallelism: Int, name: String?): CoroutineDispatcher {
        return unlimitedPool.limitedParallelism(parallelism, name)
    }
    ...
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("limitedParallelism")
        }
        org.jetbrains.compose.web.dom.Text(" does not shrink the pool; it caps how many of its workers may run ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("this view's")
        }
        org.jetbrains.compose.web.dom.Text(" tasks at once. Note the override: a limited view is built over ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("unlimitedPool")
        }
        org.jetbrains.compose.web.dom.Text(", not over the already-64-limited ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("io")
        }
        org.jetbrains.compose.web.dom.Text(". ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Every view shares one set of workers")
        }
        org.jetbrains.compose.web.dom.Text(", so the pool's size is the combined concurrent demand across all of them — which matters later, and cost me a wrong conclusion first.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For dispatchers in your own code this is one line and public API:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""private val io = Dispatchers.IO.limitedParallelism(4)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For the CIO engine it is not, because the engine's dispatcher is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("internal")
        }
        org.jetbrains.compose.web.dom.Text(". Measuring it meant forking ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-server-cio")
        }
        org.jetbrains.compose.web.dom.Text(" and replacing three lines of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("posix/src/io/ktor/server/cio/internal/CoroutineUtilsNix.kt")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""private val limitedIO: CoroutineDispatcher = Dispatchers.IO.limitedParallelism(4)

internal actual val Dispatchers.IOBridge: CoroutineDispatcher
    get() = limitedIO
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("val")
        }
        org.jetbrains.compose.web.dom.Text(" matters: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("IOBridge")
        }
        org.jetbrains.compose.web.dom.Text(" is a property with a getter, so calling ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("limitedParallelism")
        }
        org.jetbrains.compose.web.dom.Text(" inside it builds a new limiting dispatcher on every access — a leak, and a different program from the one you meant to measure.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-value-is-worth") }) {
        org.jetbrains.compose.web.dom.Text("What the value is worth")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Eight repetitions per arm, 2 000 rps offered, 200 connections, 512 MiB, all forty runs alive at 0.00 % failed. One binary throughout: the parallelism is read from an environment variable, so the arms — including the uncapped control — differ by that variable and by nothing else.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("parallelism")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("peak RSS")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("threads")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("4")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("28.5 MB")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("26")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("5.53 ms")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("213 %")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("33.7 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("34")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.68 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("221 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("16")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("43.2 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("49")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7.93 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("223 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("32")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("51.5 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("64")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.96 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("223 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("uncapped")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("63.8 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("80.5")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9.32 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("224 %")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Monotone in all four columns: less parallelism is less of everything. Against a hard-coded 8, a parallelism of 4 is better on every axis measured — memory, threads, p99 and CPU — with permutation p < 0.025 on each.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("it-does-not-cost-capacity") }) {
        org.jetbrains.compose.web.dom.Text("It does not cost capacity")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The obvious objection is that a cap low enough to save memory is free only until the offered rate rises. Three arms up a ladder of rates, three repetitions each, 200 connections; the measure is the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("shortfall")
        }
        org.jetbrains.compose.web.dom.Text(" between what was offered and what was delivered. The rungs stop at 4 000 rps because a Go control on the same pair of machines put 8 000 rps through at 261 % CPU — four cores is 400 %, and rungs above 4 000 would have found every arm equally stuck against the CPU rather than against the dispatcher.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("offered")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("arm")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("delivered")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("peak RSS")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3 500 rps")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("4")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("105 193 of 105 000")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10.1 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("242 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("37.7 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td()
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("105 122")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("12.2 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("254 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("45.3 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td()
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("uncapped")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("105 042")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("49.3 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("262 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("76.1 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("4 000 rps")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("4")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("120 160 of 120 000")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("33.0 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("247 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("43.2 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td()
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("120 137")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("20.2 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("259 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("44.4 MB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td()
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("uncapped")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("117 546")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("69.1 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("268 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("95.5 MB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The uncapped arm is the only one short on all three of its runs at the top rung — 115 806 to 117 982 against 118 465 to 120 193 for a parallelism of 4 — and its median p99 there is twice the parallelism-4 arm's and three times the parallelism-8 arm's. Three runs per cell is a small sample and a 2 % shortfall is a small effect, so this is a consistent ordering across nine runs rather than a capacity figure. It is enough for the negative: the failure mode the objection describes does not appear anywhere this stand can reach.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two honest notes. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Four is not uniformly better than eight here")
        }
        org.jetbrains.compose.web.dom.Text(" the way it was at 2 000 rps — at the top rung its median p99 is the worse of the two, and the ordering that holds all the way up is capped against uncapped, not four against eight. And these are medians of three runs against the sweep's eight: the ladder's own 2 000 rps cell gives 31.6 MB and 5.57 ms for a parallelism of 4 where the sweep gives 28.5 MB and 5.53 ms, with the thread count the noisy column in both, 34 against 26.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-cap-was-reaching-half-of-what-spawns-threads") }) {
        org.jetbrains.compose.web.dom.Text("The cap was reaching half of what spawns threads")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The table above has a loose end. Connections are fixed at 200 there; hold them fixed and raise the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("rate")
        }
        org.jetbrains.compose.web.dom.Text(" instead, and the thread count moves anyway — 32, 33, 47, 63 for a parallelism of four as the offered rate climbs. A cap that bounds concurrent tasks at four should not permit sixty-three threads.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The answer is not in the data, it is in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-network")
        }
        org.jetbrains.compose.web.dom.Text(". ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("posix/src/io/ktor/network/sockets/")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CIOReader.kt")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CIOWriter.kt")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""): WriterJob = writer(Dispatchers.IO, userChannel) {
): ReaderJob = reader(Dispatchers.IO, userChannel) {
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every accepted socket gets a read pump and a write pump on a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("raw, uncapped ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
            }
        }
        org.jetbrains.compose.web.dom.Text(" — one module below anything ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-server-cio")
        }
        org.jetbrains.compose.web.dom.Text(" can reach. The engine's limited view and the pumps' default 64-limited view share one pool of workers, as the override above shows, so capping the engine never constrained the pumps at all.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Routing all three through a single ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("newFixedThreadPoolContext")
        }
        org.jetbrains.compose.web.dom.Text(" — declared in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-utils")
        }
        org.jetbrains.compose.web.dom.Text(", the one module both depend on — changes the shape rather than the slope. Four repetitions per cell, 200 connections:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("arm")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("threads at 2 000 rps")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("at 4 000 rps")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("peak RSS")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("fixed pool, 2 workers")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("9")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("16.4 → 18.5 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.55 → 5.48 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("fixed pool, 4")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("11")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("11")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("17.8 → 20.4 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.84 → 5.86 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("fixed pool, 8")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("15")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("15")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("20.6 → 25.0 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.19 → 9.29 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("limitedParallelism(4)")
                    }
                    org.jetbrains.compose.web.dom.Text(", engine only")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("25")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("39")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("27.1 → 35.2 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.33 → 12.88 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("stock")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("80")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("108")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("62.7 → 83.4 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.41 → 71.94 ms")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Doubling the offered rate moves a fixed pool by ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("at most one thread")
        }
        org.jetbrains.compose.web.dom.Text(". It moves the engine-only cap by fourteen and stock by twenty-eight, and stock is also the only arm that fails to deliver the offered rate — 117 058 requests of 120 000 here, 117 546 in the ladder above, at a p99 of 72 ms. Those are two different sweeps of the same cell, which is about as close as this stand reproduces.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("against-go") }) {
        org.jetbrains.compose.web.dom.Text("Against Go")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same stand, same afternoon, 200 connections at 2 000 rps: Go twelve runs, the two-worker fixed pool four. Ranges rather than medians, because that is what decides whether a difference is there at all:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Go")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("fixed pool")
                }
                org.jetbrains.compose.web.dom.Th()
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("threads")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9–10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("fewer, no overlap")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.91–37.71 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("3.33–4.19 ms")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("lower, no overlap")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("peak RSS")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9.6–10.1 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("16.2–17.5 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1.7× Go")
                    }
                    org.jetbrains.compose.web.dom.Text(", no overlap")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CPU")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("139.2–251.1 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("173.8–178.3 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("not separable")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On threads and on latency a Kotlin/Native Ktor service is past Go here — its worst p99 run is below Go's best. I make ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("no claim about CPU")
        }
        org.jetbrains.compose.web.dom.Text(": Go's own spread on this stand covers the Kotlin range entirely, and a difference you cannot see through the noise is not a difference.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What is left is memory, and it is no longer threads — eight is fewer than Go holds. It is the non-per-thread part of the anonymous memory, about 6.7 MB, plus the 6 MB binary. No dispatcher reaches either, and I have not looked at what they are.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("ktor-gives-you-no-supported-way-to-do-this") }) {
        org.jetbrains.compose.web.dom.Text("Ktor gives you no supported way to do this")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything above needs a fork, and that is worth a section because it is not an oversight of one missing parameter.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The base ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ApplicationEngine.Configuration")
        }
        org.jetbrains.compose.web.dom.Text(" — the one every engine's configuration extends, in common code — already declares three public, documented properties for exactly this: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("connectionGroupSize")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("workerGroupSize")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("callGroupSize")
        }
        org.jetbrains.compose.web.dom.Text(". Netty reads all three. Across ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-server-cio")
        }
        org.jetbrains.compose.web.dom.Text(", excluding tests, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("they appear zero times")
        }
        org.jetbrains.compose.web.dom.Text(". Set ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("callGroupSize")
        }
        org.jetbrains.compose.web.dom.Text(" on a CIO server and nothing happens and nothing says so.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The property that would have worked instead is discarded. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CIOApplicationEngine")
        }
        org.jetbrains.compose.web.dom.Text(" builds its scope as")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""CoroutineScope(applicationProvider().parentCoroutineContext + engineDispatcher)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("and in a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CoroutineContext")
        }
        org.jetbrains.compose.web.dom.Text(" the right-hand operand wins, so a dispatcher supplied through the public ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("parentCoroutineContext")
        }
        org.jetbrains.compose.web.dom.Text(" is overridden. The handler is not an escape either: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("handleRequest")
        }
        org.jetbrains.compose.web.dom.Text(" opens with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("withContext(userDispatcher)")
        }
        org.jetbrains.compose.web.dom.Text(", and that is the same engine dispatcher.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is not a Kotlin/Native matter — that file is in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("common")
        }
        org.jetbrains.compose.web.dom.Text(", so it holds for CIO on the JVM too.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("and-there-is-no-laziness-to-configure") }) {
        org.jetbrains.compose.web.dom.Text("And there is no laziness to configure")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The other way to hold a thread count down is to make the dispatcher slower to create threads. That does not exist either. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("native/src/MultithreadedDispatchers.kt")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""override fun dispatch(context: CoroutineContext, block: Runnable) {
    val state = tasksAndWorkersCounter.getAndUpdate { ... it + 2 }
    if (state.hasWorkers()) {
        obtainWorker().resume(block)
    } else {
        workerPool.allocate()
        // no workers are available, we must queue the task
        val result = tasksQueue.trySend(block)
        checkChannelResult(result)
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A task arriving while every worker is busy ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("creates a thread immediately")
        }
        org.jetbrains.compose.web.dom.Text(", up to the cap — no queue-depth threshold, no delay, no waiting to see whether one frees up. And ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("workerRunLoop()")
        }
        org.jetbrains.compose.web.dom.Text(" is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("while (true)")
        }
        org.jetbrains.compose.web.dom.Text(" whose only exit is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("close()")
        }
        org.jetbrains.compose.web.dom.Text(": a worker is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("never retired for being idle")
        }
        org.jetbrains.compose.web.dom.Text(". The class takes ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("(name, workersCount)")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("workersCount")
        }
        org.jetbrains.compose.web.dom.Text(" is its only tuning parameter. There is no core-versus-max, no keep-alive, no scheduler policy to tune.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Which makes the missing retirement the more useful half of the finding. Because a worker never goes away, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("the thread count is a high-water mark, not a steady state")
        }
        org.jetbrains.compose.web.dom.Text(": a dispatcher that grew only under genuine pressure would still sit at the level of the day's worst spike for the rest of the day. Laziness would postpone the ratchet; only a cap bounds it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is the third mechanism here with that shape. The allocator's per-thread pages are filled on first use and never returned; glibc's arenas release only from their top; a worker never exits. All three make resident memory a function of the worst moment the process has lived through.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-none-of-this-is-filed-upstream") }) {
        org.jetbrains.compose.web.dom.Text("Why none of this is filed upstream")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two things are missing, and both are the kind a reviewer asks about first.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Four is this machine's core count.")
        }
        org.jetbrains.compose.web.dom.Text(" On four cores, \"a parallelism of 4\" and \"a parallelism of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("availableProcessors")
        }
        org.jetbrains.compose.web.dom.Text("\" are the same arm, and nothing measured here separates a good constant from a rule. That is the difference between a number every user has to tune and a default that is right everywhere, and settling it needs a machine with a different core count, which I do not have.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The threads are not named.")
        }
        org.jetbrains.compose.web.dom.Text(" Kotlin/Native names exactly three threads in the process — two GC threads and a GC timer — and leaves every other thread carrying the truncated process name, so ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc")
        }
        org.jetbrains.compose.web.dom.Text(" cannot tell an IO worker from a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.Default")
        }
        org.jetbrains.compose.web.dom.Text(" worker. A change argued from a thread count ought to be able to name the threads it removes, and this one cannot yet.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And the sharpest reason arrived with the best result. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A pool that cannot grow cannot absorb a blocking call.")
        }
        org.jetbrains.compose.web.dom.Text(" With N fixed workers, N concurrent blocking syscalls do not slow the server down, they stop it. Go is safe from this because its runtime takes the processor away from a thread parked in a syscall and gives it to another; Kotlin/Native has nobody to take it away. This probe blocks nowhere — one route, one JSON object, no disk, no C library — so ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("the stand cannot see that failure at all.")
        }
        org.jetbrains.compose.web.dom.Text(" Every number in this post comes from the half of the space where the change is free.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is a limit of the measurement rather than a caveat about its precision, and on its own it disqualifies any of this as a default. A service that reads a file, calls into C, or resolves a name on a request path is the ordinary case, and it is the case this work excludes by construction.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-to-do-today") }) {
        org.jetbrains.compose.web.dom.Text("What to do today")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("For dispatchers you own, cap them")
            }
            org.jetbrains.compose.web.dom.Text(" — and prefer ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("newFixedThreadPoolContext(n)")
            }
            org.jetbrains.compose.web.dom.Text(" to ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Dispatchers.IO.limitedParallelism(n)")
            }
            org.jetbrains.compose.web.dom.Text(" when you want a bound on ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("threads")
            }
            org.jetbrains.compose.web.dom.Text(" rather than on concurrency, remembering that a fixed pool is a deadlock waiting for a blocking call.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Do not expect the engine knobs to do anything on CIO.")
            }
            org.jetbrains.compose.web.dom.Text(" Three of them exist and are inert.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Size containers from a loaded process.")
            }
            org.jetbrains.compose.web.dom.Text(" The resting footprint of a Kotlin/Native service is not a smaller version of its working footprint; here it was a fifth of it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Bound the connections instead")
            }
            org.jetbrains.compose.web.dom.Text(", if you are on a stock Ktor. That was the previous post's recommendation and it is still the one thing that works without a fork: threads follow concurrency, and a reverse proxy with a bounded keep-alive pool turns a variable you do not control into one you do.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" is the only target measured, and four cores the only machine — which is also why the strongest result here is not a proposal.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One route returning one small JSON object is the only workload. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A handler that blocks is precisely the case ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
            }
            org.jetbrains.compose.web.dom.Text(" exists for, and a cap of four would be the wrong setting for it")
        }
        org.jetbrains.compose.web.dom.Text("; everything above is about a service whose work is parsing and serialising.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every rate is one the generator held at the stated value, so no arm was run to its actual ceiling: the ladder shows which arm falls behind first, not what any of them can do.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The source quotations are from kotlinx.coroutines 1.11.0 and Ktor 3.5.2, the versions the probe resolves. The claim that CIO ignores the three engine knobs follows from those names appearing nowhere in the module, which is strong but is not the same as running a server with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("callGroupSize")
        }
        org.jetbrains.compose.web.dom.Text(" set and counting its threads. And every peak is over a 30-second run at a steady rate — nothing here says what a day-long process does, and a page cache filled on first use and never returned is exactly the kind of thing that could behave differently over hours.")
    }
}
